/* =============================================================
   Filter logic — apply a user's Selection to the structured data
   and return only the categories/sections/items they need to see.

   Kept pure (no React, no DOM, no storage). Easy to unit test, and
   it's the same logic that will run server-side later when exports
   are generated.
   ============================================================ */

import type {
  Applicability,
  Category,
  ChecklistCategory,
  ChecklistSection,
  Selection,
} from './types';

/** True when the item passes the applicability gate for this selection. */
export function passes(rule: Applicability | undefined, sel: Selection): boolean {
  if (!rule) return true; // no rule = always applies

  if (rule.pathways && rule.pathways.length > 0) {
    if (!sel.pathway || !rule.pathways.includes(sel.pathway)) return false;
  }

  if (rule.subPathwaysC && rule.subPathwaysC.length > 0) {
    // Only meaningful when on Pathway C — but in practice these rules
    // are always paired with pathways:['C'] so we don't gate-by-pathway here.
    if (!sel.subPathwayC || !rule.subPathwaysC.includes(sel.subPathwayC)) return false;
  }

  if (rule.scenarios && rule.scenarios.length > 0) {
    const overlap = sel.scenarios.some((s) => rule.scenarios!.includes(s));
    if (!overlap) return false;
  }

  return true;
}

/**
 * Walk the full checklist and return only the parts visible for this selection.
 * Categories/sections with no surviving items are dropped from the result.
 */
export function filterChecklist(
  data: ChecklistCategory[],
  selection: Selection,
): ChecklistCategory[] {
  const out: ChecklistCategory[] = [];

  for (const cat of data) {
    if (!passes(cat.appliesTo, selection)) continue;

    const sections: ChecklistSection[] = [];
    for (const sec of cat.sections) {
      if (!passes(sec.appliesTo, selection)) continue;
      const items = sec.items.filter((it) => passes(it.appliesTo, selection));
      if (items.length === 0) continue;
      sections.push({ ...sec, items });
    }

    if (sections.length === 0) continue;
    out.push({ ...cat, sections });
  }

  return out;
}

/** Count visible items in a filtered category set. */
export function countItems(filtered: ChecklistCategory[]): number {
  let n = 0;
  for (const c of filtered) for (const s of c.sections) n += s.items.length;
  return n;
}

/** Split a filtered set by top-tab bucket. */
export function bucketByCategory(
  filtered: ChecklistCategory[],
): Record<Category, ChecklistCategory[]> {
  const out: Record<Category, ChecklistCategory[]> = {
    onboarding: [],
    optional: [],
    operational: [],
  };
  for (const c of filtered) out[c.category].push(c);
  return out;
}

/** Per-category item counts for the tab badges. */
export function countByCategory(
  filtered: ChecklistCategory[],
): Record<Category, number> {
  const buckets = bucketByCategory(filtered);
  return {
    onboarding: countItems(buckets.onboarding),
    optional: countItems(buckets.optional),
    operational: countItems(buckets.operational),
  };
}
